/**
 * The value row (SHELL_CHROME_SPEC §12) — slider + numeric entry as ONE 22px
 * compound. Two doors, one commit: the slider previews on drag and commits
 * once on release (the shipped Character-panel gesture contract); the field
 * commits on Enter/blur, reverts on Escape, steps on arrows (Shift ×10) —
 * and BOTH doors write the same command through the caller's `commit`, so a
 * gesture is one undo step whichever door it came through.
 *
 * Out-of-band entry clamps on commit and the field shows the clamped value
 * immediately — visible truth, never silently different from what was typed
 * (§12.2). Non-numeric entry reverts, never NaN. The optional provenance
 * chip (§12.5) keeps a measured default legible as measurement: evidence
 * stays on the surface; only explanation moves to tooltips.
 *
 * Depends on: tooltip (tier-1 tip + tier-2 door). Depended on by: panels
 * (Character first).
 */
import { attachTip, describeButton } from './tooltip.js';

/** Resolve a field commit: parse, clamp to the band, quantize display. */
export function resolveFieldEntry(
  text: string,
  band: { readonly min: number; readonly max: number },
): { kind: 'commit'; value: number } | { kind: 'revert' } {
  const value = Number(text.trim());
  if (text.trim() === '' || !Number.isFinite(value)) return { kind: 'revert' };
  return { kind: 'commit', value: Math.min(band.max, Math.max(band.min, value)) };
}

/** One arrow step from a current value, clamped. `shift` multiplies ×10. */
export function stepValue(
  current: number,
  direction: 1 | -1,
  step: number,
  shift: boolean,
  band: { readonly min: number; readonly max: number },
): number {
  const next = current + direction * step * (shift ? 10 : 1);
  return Math.min(band.max, Math.max(band.min, next));
}

export interface ValueRowState {
  readonly kind: 'ready' | 'refused';
  /** Present when ready. */
  readonly value?: number;
  /** Present when refused — rendered as the evidence line (§13.4). */
  readonly reason?: string;
  /** Optional §12.5 provenance chip text (e.g. 'baseline' | 'set'); null hides. */
  readonly chip?: string | null;
}

export interface ValueRowSpec {
  readonly label: string;
  readonly unit: string;
  readonly min: number;
  readonly max: number;
  readonly step: number;
  /** Digits shown in the field (values are truth: display rounds, commit does not). */
  readonly precision: number;
  /** Tier-1 tip — a few words. */
  readonly tip: string;
  /** Tier-2 explanation, read at open time so live values never go stale. */
  readonly describe: () => string;
  readonly state: () => ValueRowState;
  readonly commit: (value: number) => void;
  readonly preview: (value: number | null) => void;
}

export interface ValueRowHandle {
  readonly root: HTMLDivElement;
  /** The refusal line lives OUTSIDE the row (block layout); caller appends both. */
  readonly refusal: HTMLParagraphElement;
  render(): void;
}

export function createValueRow(spec: ValueRowSpec): ValueRowHandle {
  const root = document.createElement('div');
  root.className = 'value-row';
  root.setAttribute('role', 'group');

  const label = document.createElement('span');
  label.className = 'vr-label';
  label.textContent = spec.label;
  label.id = `vr-label-${spec.label.replace(/\W+/g, '-').toLowerCase()}`;
  root.setAttribute('aria-labelledby', label.id);

  const slider = document.createElement('input');
  slider.type = 'range';
  slider.min = String(spec.min);
  slider.max = String(spec.max);
  slider.step = String(spec.step);
  slider.setAttribute('aria-label', `${spec.label} degrees`);

  const field = document.createElement('input');
  field.type = 'text';
  field.inputMode = 'decimal';
  field.className = 'vr-field';
  field.setAttribute('aria-label', `${spec.label} value`);

  const unit = document.createElement('span');
  unit.className = 'vr-unit';
  unit.textContent = spec.unit;

  const chip = document.createElement('span');
  chip.className = 'vr-chip';
  chip.hidden = true;

  const describe = describeButton(spec.label, spec.describe);

  root.append(label, slider, field, unit, chip, describe);
  const tipNode = attachTip(slider, spec.tip);
  root.appendChild(tipNode);
  field.setAttribute('aria-describedby', tipNode.id);

  const refusal = document.createElement('p');
  refusal.className = 'value-refusal';
  refusal.hidden = true;

  const paintFill = (value: number): void => {
    const pct = ((value - spec.min) / (spec.max - spec.min)) * 100;
    slider.style.setProperty('--vr-fill', `${Math.max(0, Math.min(100, pct)).toFixed(1)}%`);
  };
  const showValue = (value: number): void => {
    slider.value = String(value);
    field.value = value.toFixed(spec.precision);
    paintFill(value);
  };

  // Door 1 — the slider: preview under the thumb, ONE commit on release.
  slider.addEventListener('input', () => {
    const v = Number(slider.value);
    field.value = v.toFixed(spec.precision);
    paintFill(v);
    spec.preview(v);
  });
  slider.addEventListener('change', () => {
    spec.commit(Number(slider.value));
  });

  // Door 2 — the field.
  const commitField = (): void => {
    const state = spec.state();
    const current = state.value ?? 0;
    const result = resolveFieldEntry(field.value, spec);
    if (result.kind === 'revert') {
      showValue(current);
      return;
    }
    if (result.value === current) {
      showValue(current); // normalize the display; nothing to commit
      return;
    }
    spec.commit(result.value);
  };
  field.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      commitField();
      return;
    }
    if (e.key === 'Escape') {
      const state = spec.state();
      showValue(state.value ?? 0);
      return;
    }
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const state = spec.state();
      const next = stepValue(state.value ?? 0, e.key === 'ArrowUp' ? 1 : -1, spec.step, e.shiftKey, spec);
      if (next !== (state.value ?? 0)) {
        spec.commit(next);
        // Sync the focused field NOW (Codex P2, #78): render() deliberately
        // never rewrites a focused field, so without this the old text
        // survives the commit — and the eventual blur would parse it and
        // commit the stale value back, silently undoing the keyboard step.
        showValue(next);
      }
    }
  });
  field.addEventListener('blur', commitField);

  const render = (): void => {
    const state = spec.state();
    if (state.kind === 'refused') {
      slider.disabled = true;
      field.disabled = true;
      root.dataset['disabled'] = 'true';
      refusal.hidden = false;
      // The reason names its own subject (a rig without the mapping, a
      // material without the texture) — the prefix stays generic.
      refusal.textContent = `Unavailable: ${state.reason ?? ''}`;
      chip.hidden = true;
      return;
    }
    slider.disabled = false;
    field.disabled = false;
    root.dataset['disabled'] = 'false';
    refusal.hidden = true;
    // Never yank the field mid-edit: while it holds focus, the person is the
    // author of its contents; the commit/revert handlers own the sync.
    if (document.activeElement !== field) showValue(state.value ?? 0);
    else paintFill(state.value ?? 0);
    if (state.chip === null || state.chip === undefined) {
      chip.hidden = true;
    } else {
      chip.hidden = false;
      chip.textContent = state.chip;
    }
  };

  return { root, refusal, render };
}
